import React, { useState, useEffect } from 'react';
import api from '../api';

const IconRefresh = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <polyline points="23 4 23 10 17 10"/>
    <polyline points="1 20 1 14 7 14"/>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
  </svg>
);

const IconSearch = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/>
    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const IconLogs = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <line x1="10" y1="9" x2="8" y2="9"/>
  </svg>
);

// ── Action classification ──────────────────────────────────────────────────
const ACTION_META = {
  LOGIN:              { cls: 'action-login',  label: 'Login'      },
  LOGIN_FAILED:       { cls: 'action-delete', label: 'Login Failed' },
  LOGOUT:             { cls: 'action-login',  label: 'Logout'     },
  REGISTER:           { cls: 'action-add',    label: 'Register'   },
  ADD_USER:           { cls: 'action-add',    label: 'Add User'   },
  DELETE_USER:        { cls: 'action-delete', label: 'Del User'   },
  UPDATE_USER_ROLE:   { cls: 'action-update', label: 'Role Change'},
  ADD_UPTIME_MONITOR: { cls: 'action-add',    label: 'Add Monitor'},
  DELETE_UPTIME_MONITOR:{ cls:'action-delete',label: 'Del Monitor'},
  ADD_DNS_MONITOR:    { cls: 'action-add',    label: 'Add DNS'    },
  BULK_ADD_DNS:       { cls: 'action-add',    label: 'Bulk Import'},
  DELETE_DNS_MONITOR: { cls: 'action-delete', label: 'Del DNS'    },
  DNS_CHECK:          { cls: 'action-check',  label: 'DNS Check'  },
  DNS_CHECK_ALL:      { cls: 'action-check',  label: 'Check All'  },
};

function getActionMeta(action) {
  if (!action) return { cls: 'action-default', label: action };
  return ACTION_META[action] || { cls: 'action-default', label: action };
}

function formatTs(dateStr) {
  return new Date(dateStr).toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

// ── Date helpers ───────────────────────────────────────────────────────────
function toLocalDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function todayStr()     { return toLocalDateStr(new Date()); }
function yesterday()    { const d = new Date(); d.setDate(d.getDate() - 1); return toLocalDateStr(d); }
function last7days()    { const d = new Date(); d.setDate(d.getDate() - 7); return toLocalDateStr(d); }

// ── Component ──────────────────────────────────────────────────────────────
function SystemLogs({ token }) {
  const [logs, setLogs]                 = useState([]);
  const [loading, setLoading]           = useState(true);
  const [searchTerm, setSearchTerm]     = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterUser, setFilterUser]     = useState('');
  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');

  useEffect(() => {
    fetchLogs();
    const interval = setInterval(fetchLogs, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchLogs = async () => {
    try {
      const res = await api.get('/logs');
      setLogs(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterAction('');
    setFilterUser('');
    setDateFrom('');
    setDateTo('');
  };

  const hasFilters = searchTerm || filterAction || filterUser || dateFrom || dateTo;

  // ── Derived filter options ─────────────────────────────────────────────
  const uniqueActions = [...new Set(logs.map(l => l.action).filter(Boolean))].sort();
  const uniqueUsers   = [...new Set(logs.map(l => l.user_name).filter(Boolean))].sort();

  // ── Filtering ──────────────────────────────────────────────────────────
  const filteredLogs = logs.filter(log => {
    if (filterAction && log.action !== filterAction) return false;
    if (filterUser   && log.user_name !== filterUser) return false;

    if (dateFrom) {
      const logDate = new Date(log.created_at);
      const from    = new Date(dateFrom + 'T00:00:00');
      if (logDate < from) return false;
    }
    if (dateTo) {
      const logDate = new Date(log.created_at);
      const to      = new Date(dateTo + 'T23:59:59');
      if (logDate > to) return false;
    }

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      return (
        log.action?.toLowerCase().includes(q) ||
        log.details?.toLowerCase().includes(q) ||
        log.user_name?.toLowerCase().includes(q) ||
        log.ip_address?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  if (loading) {
    return <div className="loading"><div className="spinner" /><span>Loading logs…</span></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-header-top">
          <div>
            <h1>System Logs</h1>
            <p>Audit trail of all system activities and changes</p>
          </div>
          <div className="page-actions">
            <button className="btn btn-secondary" onClick={fetchLogs}>
              <IconRefresh /> Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="table-container">
        <div className="table-header" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
            <h3>
              Events
              {hasFilters && (
                <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6 }}>
                  · {filteredLogs.length} of {logs.length}
                </span>
              )}
            </h3>
            {hasFilters && (
              <button className="btn btn-ghost btn-sm" onClick={clearFilters}>Clear all filters</button>
            )}
          </div>

          {/* Filter row */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, width: '100%' }}>
            <div className="search-input-wrap" style={{ flex: '1 1 180px', minWidth: 160 }}>
              <IconSearch />
              <input type="text" placeholder="Search…" value={searchTerm}
                     onChange={e => setSearchTerm(e.target.value)} />
            </div>

            <select className="filter-select" value={filterAction}
                    onChange={e => setFilterAction(e.target.value)} style={{ flex: '0 0 auto' }}>
              <option value="">All actions</option>
              {uniqueActions.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>

            <select className="filter-select" value={filterUser}
                    onChange={e => setFilterUser(e.target.value)} style={{ flex: '0 0 auto' }}>
              <option value="">All users</option>
              {uniqueUsers.map(u => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>

            {/* Date range quick shortcuts */}
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              {[
                { label: 'Today',     from: todayStr(),  to: todayStr() },
                { label: 'Yesterday', from: yesterday(), to: yesterday() },
                { label: '7 days',    from: last7days(), to: todayStr() },
              ].map(({ label, from, to }) => (
                <button key={label}
                  className={`btn btn-sm ${dateFrom === from && dateTo === to ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => {
                    if (dateFrom === from && dateTo === to) { setDateFrom(''); setDateTo(''); }
                    else { setDateFrom(from); setDateTo(to); }
                  }}
                >{label}</button>
              ))}
            </div>

            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                   title="From date"
                   style={{ padding: '7px 10px', border: '1px solid var(--border)',
                            borderRadius: 'var(--radius-sm)', background: 'var(--bg-input)',
                            color: 'var(--text-primary)', fontSize: 13, fontFamily: 'var(--font)' }} />
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                   title="To date"
                   style={{ padding: '7px 10px', border: '1px solid var(--border)',
                            borderRadius: 'var(--radius-sm)', background: 'var(--bg-input)',
                            color: 'var(--text-primary)', fontSize: 13, fontFamily: 'var(--font)' }} />
          </div>
        </div>

        {filteredLogs.length === 0 ? (
          <div className="table-empty">
            <div className="table-empty-icon"><IconLogs /></div>
            <div className="table-empty-title">
              {hasFilters ? 'No matching events' : 'No activity logged yet'}
            </div>
            <div className="table-empty-desc">
              {hasFilters
                ? 'Try adjusting your filters'
                : 'System events will appear here as actions are taken'}
            </div>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: 160 }}>Timestamp</th>
                <th style={{ width: 120 }}>User</th>
                <th style={{ width: 150 }}>Action</th>
                <th>Details</th>
                <th style={{ width: 120 }}>IP Address</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map(log => {
                const meta = getActionMeta(log.action);
                return (
                  <tr key={log.id}>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap',
                                 fontFamily: 'var(--font-mono)' }}>
                      {formatTs(log.created_at)}
                    </td>
                    <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                      {log.user_name || (
                        <span style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: 12 }}>system</span>
                      )}
                    </td>
                    <td>
                      <span className={`action-badge ${meta.cls}`}>
                        {log.action}
                      </span>
                    </td>
                    <td style={{ color: 'var(--text-secondary)', maxWidth: 300 }}>
                      <span style={{ display: 'block', overflow: 'hidden',
                                     textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {log.details || '-'}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
                      {log.ip_address || '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <div className="table-footer">
          {filteredLogs.length === logs.length
            ? `${logs.length} total events`
            : `Showing ${filteredLogs.length} of ${logs.length} events`}
        </div>
      </div>
    </div>
  );
}

export default SystemLogs;
